/**
 * Voice Gateway Service v7
 *
 * CHANGES FROM v6:
 *   1. MIC MUTE/UNMUTE — new muteMic() / unmuteMic() / toggleMute() methods.
 *      When muted, the audio processor stops sending PCM data to the gateway
 *      but the MediaStream and AudioContext stay alive (no teardown/re-init).
 *      This lets users mute during long backend queries and unmute when ready.
 *   2. 'mute_changed' event — emitted whenever mute state changes, carrying
 *      { muted: boolean } so the frontend can update UI instantly.
 *   3. All v6 features preserved: output_transcript_chunk, voice_done,
 *      direct_done, etc.
 */

import { io } from 'socket.io-client';
import config from '../config';

const MIN_QUESTION_LENGTH = 3;

class VoiceGatewayService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = {};

    this.inputAudioCtx = null;
    this.outputAudioCtx = null;
    this.outputGainNode = null;
    this.mediaStream = null;
    this.processor = null;
    this.sourceNode = null;
    this.isStreaming = false;

    // ── NEW v7: Mute state ──────────────────────────────────
    this.muted = false;

    this.nextPlayTime = 0;
    this.activeSources = new Set();

    this.inputBuf = '';
    this.outputBuf = '';
    // Phases: idle | ack | waiting | followup | data | direct
    this.backendPhase = 'idle';
    this.pendingDataText = null;

    this.geminiAlive = false;
    this._connectConfig = null;
  }

  async connect({ appId, targetLanguage, userId }) {
    if (this.socket?.connected) return;
    const url = config.voiceGatewayUrl || 'wss://voice-gateway.varunj.ai';

    this._connectConfig = {
      appId: appId || config.voiceGatewayAppId,
      targetLanguage: targetLanguage || 'hi-IN',
      userId,
    };

    return new Promise((resolve, reject) => {
      this.socket = io(url, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      this.socket.on('connect', () => {
        this.connected = true;
        this.socket.emit('configure_session', this._connectConfig);
      });

      this.socket.on('live_session_ready', (data) => {
        this.geminiAlive = true;
        this._emit('ready', data);
        resolve();
      });

      this.socket.on('session_configured', (data) => this._emit('configured', data));

      this.socket.on('live_session_ended', (data) => {
        this.geminiAlive = false;
        console.log('[Voice] Gemini session ended, code:', data?.code);
        if (this.isStreaming) {
          this._cleanup();
          this._emit('session_expired', { code: data?.code });
        }
      });

      this.socket.on('audio_data', (data) => {
        this._playAudio(data);
      });

      // ── Transcription ───────────────────────────────────────
      this.socket.on('input_transcription', (data) => {
        if (data?.text) {
          this.inputBuf += data.text;
          this._emit('live_transcript', { text: this.inputBuf });
        }
      });

      this.socket.on('output_transcription', (data) => {
        if (data?.text && (this.backendPhase === 'data' || this.backendPhase === 'direct')) {
          this.outputBuf += data.text;

          // ── v6: Emit progressive transcript chunk ──────
          this._emit('output_transcript_chunk', {
            text: this.outputBuf,
            phase: this.backendPhase,
          });
        }
      });

      // ── Turn complete ───────────────────────────────────────
      this.socket.on('turn_complete', () => {
        const userText = this.inputBuf.trim();
        this.inputBuf = '';

        if (userText) {
          if (userText.length < MIN_QUESTION_LENGTH) {
            console.log('[Voice] Ignoring short transcript (noise):', userText);
            return;
          }
          console.log('[Voice] User asked:', userText);
          this._emit('user_question', { text: userText });
          return;
        }

        // Data speech finished — emit summary text
        if (this.backendPhase === 'data') {
          const summary = this.outputBuf.trim();
          this.outputBuf = '';
          this.backendPhase = 'idle';
          this.pendingDataText = null;
          console.log('[Voice] AI summary:', summary.substring(0, 80));
          this._emit('voice_done', { summary });
          return;
        }

        // Direct (non-sales) speech finished
        if (this.backendPhase === 'direct') {
          const response = this.outputBuf.trim();
          this.outputBuf = '';
          this.backendPhase = 'idle';
          console.log('[Voice] Direct response:', response.substring(0, 80));
          this._emit('direct_done', { text: response });
          return;
        }

        // Ack/followup finished
        if (this.backendPhase === 'ack' || this.backendPhase === 'followup') {
          if (this.pendingDataText) {
            const text = this.pendingDataText;
            this.pendingDataText = null;
            this._sendDataNow(text);
          } else {
            this.backendPhase = 'waiting';
          }
          return;
        }

        console.log('[Voice] Turn complete in phase:', this.backendPhase);
      });

      this.socket.on('interrupted', () => {
        this._stopAllPlayback();
        this._emit('interrupted');
      });

      this.socket.on('error', (err) => this._emit('error', err));

      this.socket.on('disconnect', (reason) => {
        this.connected = false;
        this.geminiAlive = false;
        this._cleanup();
        this._emit('disconnected', { reason });
      });

      this.socket.on('reconnect', () => {
        this.connected = true;
        this.geminiAlive = false;
        if (this._connectConfig) {
          this.socket.emit('configure_session', this._connectConfig);
        }
      });

      this.socket.on('connect_error', (err) => reject(err));

      setTimeout(() => { if (!this.connected) reject(new Error('Connection timeout')); }, 15000);
    });
  }

  disconnect() {
    this._cleanup();
    if (this.socket) {
      this.socket.emit('end_session');
      this.socket.disconnect();
      this.socket = null;
    }
    this.connected = false;
    this.geminiAlive = false;
  }

  async startMic() {
    if (this.isStreaming) return;

    if (!this.geminiAlive && this.connected && this._connectConfig) {
      console.log('[Voice] Gemini session dead, reconnecting...');
      await this._reconnectGemini();
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.inputAudioCtx = new AudioContextClass({ sampleRate: 16000 });
    this.outputAudioCtx = new AudioContextClass({ sampleRate: 24000 });

    this.outputGainNode = this.outputAudioCtx.createGain();
    this.outputGainNode.connect(this.outputAudioCtx.destination);

    this.sourceNode = this.inputAudioCtx.createMediaStreamSource(this.mediaStream);
    this.processor = this.inputAudioCtx.createScriptProcessor(4096, 1, 1);

    this.processor.onaudioprocess = (e) => {
      if (!this.isStreaming || !this.connected || !this.geminiAlive) return;

      // ── v7: Skip sending audio when muted ─────────────────
      if (this.muted) return;

      const float32 = e.inputBuffer.getChannelData(0);
      const pcm16 = new Int16Array(float32.length);
      for (let i = 0; i < float32.length; i++) pcm16[i] = float32[i] * 32768;
      this.socket.emit('audio_in', {
        audio: bufferToBase64(pcm16.buffer),
        mimeType: 'audio/pcm;rate=16000',
      });
    };

    this.sourceNode.connect(this.processor);
    this.processor.connect(this.inputAudioCtx.destination);

    this.isStreaming = true;
    this.muted = false; // Always start unmuted
    this.backendPhase = 'idle';
    this.pendingDataText = null;
    this.inputBuf = '';
    this.outputBuf = '';
    this.nextPlayTime = 0;
  }

  _reconnectGemini() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Gemini reconnection timeout')), 15000);
      const onReady = () => { clearTimeout(timeout); this.geminiAlive = true; resolve(); };
      this.socket.once('live_session_ready', onReady);
      this.socket.once('error', (err) => {
        clearTimeout(timeout);
        this.socket.off('live_session_ready', onReady);
        reject(new Error(err?.message || 'Gemini reconnection failed'));
      });
      this.socket.emit('configure_session', this._connectConfig);
    });
  }

  stopMic() { this._cleanup(); }

  // ══════════════════════════════════════════════════════════════
  // NEW v7: Mute / Unmute / Toggle
  // ══════════════════════════════════════════════════════════════

  /**
   * Mute the microphone — stops sending audio to gateway but keeps
   * the MediaStream, AudioContext, and WebSocket alive.
   * Gemini continues speaking if it was mid-speech.
   */
  muteMic() {
    if (!this.isStreaming) return;
    this.muted = true;
    console.log('[Voice] Mic muted');
    this._emit('mute_changed', { muted: true });
  }

  /**
   * Unmute the microphone — resumes sending audio to gateway.
   */
  unmuteMic() {
    if (!this.isStreaming) return;
    this.muted = false;
    console.log('[Voice] Mic unmuted');
    this._emit('mute_changed', { muted: false });
  }

  /**
   * Toggle mute state. Returns the new muted value.
   */
  toggleMute() {
    if (this.muted) {
      this.unmuteMic();
    } else {
      this.muteMic();
    }
    return this.muted;
  }

  /**
   * Check if mic is currently muted.
   */
  isMuted() {
    return this.muted;
  }

  // ══════════════════════════════════════════════════════════════

  speakAck(text) {
    if (!this.connected || !this.geminiAlive || !text) return;
    this.backendPhase = 'ack';
    this.pendingDataText = null;
    this.socket.emit('speak_response', { text });
  }

  speakFollowup(text) {
    if (!this.connected || !this.geminiAlive || !text) return;
    if (this.backendPhase !== 'waiting') return;
    this.backendPhase = 'followup';
    this.socket.emit('speak_response', { text });
  }

  speakData(text) {
    if (!this.connected || !this.geminiAlive || !text) return;
    if (this.backendPhase === 'ack' || this.backendPhase === 'followup') {
      this.pendingDataText = text;
    } else {
      this._sendDataNow(text);
    }
  }

  speakDirect(text) {
    if (!this.connected || !this.geminiAlive || !text) return;
    this.backendPhase = 'direct';
    this.outputBuf = '';
    this.socket.emit('speak_response', { text });
  }

  _sendDataNow(text) {
    this.backendPhase = 'data';
    this.outputBuf = '';
    this.socket.emit('speak_response', { text });
  }

  _playAudio(data) {
    if (!data?.data || !this.outputAudioCtx || !this.outputGainNode) return;
    const raw = base64ToBuffer(data.data);
    const pcm16 = new Int16Array(raw);
    const buffer = this.outputAudioCtx.createBuffer(1, pcm16.length, 24000);
    const channelData = buffer.getChannelData(0);
    for (let i = 0; i < pcm16.length; i++) channelData[i] = pcm16[i] / 32768.0;
    const source = this.outputAudioCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.outputGainNode);
    source.addEventListener('ended', () => this.activeSources.delete(source));
    this.nextPlayTime = Math.max(this.nextPlayTime, this.outputAudioCtx.currentTime);
    source.start(this.nextPlayTime);
    this.nextPlayTime += buffer.duration;
    this.activeSources.add(source);
  }

  _stopAllPlayback() {
    for (const s of this.activeSources.values()) { try { s.stop(); } catch (e) {} this.activeSources.delete(s); }
    this.nextPlayTime = 0;
  }

  _cleanup() {
    this.isStreaming = false;
    this.muted = false; // Reset mute on cleanup
    this.backendPhase = 'idle';
    this.pendingDataText = null;
    this._stopAllPlayback();
    if (this.processor) { this.processor.disconnect(); this.processor = null; }
    if (this.sourceNode) { this.sourceNode.disconnect(); this.sourceNode = null; }
    if (this.inputAudioCtx) { this.inputAudioCtx.close().catch(() => {}); this.inputAudioCtx = null; }
    if (this.outputAudioCtx) { this.outputAudioCtx.close().catch(() => {}); this.outputAudioCtx = null; }
    this.outputGainNode = null;
    if (this.mediaStream) { this.mediaStream.getTracks().forEach(t => t.stop()); this.mediaStream = null; }
  }

  on(event, cb) { if (!this.listeners[event]) this.listeners[event] = []; this.listeners[event].push(cb); }
  off(event, cb) { if (this.listeners[event]) this.listeners[event] = this.listeners[event].filter(f => f !== cb); }
  removeAllListeners() { this.listeners = {}; }
  _emit(event, data) { (this.listeners[event] || []).forEach(cb => { try { cb(data); } catch (e) { console.error(e); } }); }
}

function bufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToBuffer(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export const voiceGatewayService = new VoiceGatewayService();
export default voiceGatewayService;
